import React, { useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import type { DashboardContextValueInterface } from '@/contexts/DashboardContext';
import type { UserLocation } from '@/domain/Location/Location';
import { publishDashboardMapEvent } from '@/events/dashboardMapEvents';
import type { MapModeId } from '@/events/dashboardMapEvents';

interface UseModeValidationsParams {
	contextValue: DashboardContextValueInterface;
	setContextValue: React.Dispatch<React.SetStateAction<DashboardContextValueInterface>>;
	locations: UserLocation[] | null;
	selectedMode: MapModeId;
	rawModeLabel: string;
	setShowInflowWarningModal: (show: boolean) => void;
	setShowDmaWarningModal: (show: boolean) => void;
}

const findLocationRecursively = (
	locations: UserLocation[],
	locationId: string,
): UserLocation | undefined => {
	for (const location of locations) {
		if (location.locationId === locationId) return location;
		if (location.childLocations?.length) {
			const found = findLocationRecursively(location.childLocations, locationId);
			if (found) return found;
		}
	}
	return undefined;
};

export function useModeValidations({
	contextValue,
	setContextValue,
	locations,
	selectedMode,
	rawModeLabel,
	setShowInflowWarningModal,
	setShowDmaWarningModal,
}: UseModeValidationsParams) {
	const { isWaterNetwork, isDrainageNetwork, analysisModeLabel } = useMemo(() => {
		if (!locations?.length || !contextValue.selectedLocation) {
			return { isWaterNetwork: false, isDrainageNetwork: true, analysisModeLabel: 'Sewer Analysis' };
		}
		const currentLocation = findLocationRecursively(locations, contextValue.selectedLocation);
		const networkType = String(currentLocation?.networkType ?? '').toUpperCase();
		const isWater = networkType === 'WATER';
		const isDrainage = networkType === 'SEWER';
		return {
			isWaterNetwork: isWater,
			isDrainageNetwork: isDrainage,
			analysisModeLabel: isWater ? 'Water Analysis' : 'Sewer Analysis',
		};
	}, [locations, contextValue.selectedLocation]);

	const selectedModeLabel = selectedMode === 'sewer-analysis' ? analysisModeLabel : rawModeLabel;

	const validateInflowMode = (): boolean => {
		let isWastewaterNetwork = false;
		if (locations && locations.length > 0 && contextValue.selectedLocation) {
			const currentLocation = findLocationRecursively(locations, contextValue.selectedLocation);
			isWastewaterNetwork = currentLocation?.networkType === 'SEWER';
		}

		let isLessThanWeek = false;
		if (contextValue.selectedDate && contextValue.selectedDate.length === 2) {
			const startDate = dayjs(contextValue.selectedDate[0]);
			const endDate = dayjs(contextValue.selectedDate[1]);
			const daysDiff = endDate.diff(startDate, 'day');
			isLessThanWeek = daysDiff < 7;
		}

		return isWastewaterNetwork && isLessThanWeek;
	};

	const validateDmaDateRange = (): boolean => {
		if (!contextValue.selectedDate || contextValue.selectedDate.length !== 2) return true;
		const startDate = dayjs(contextValue.selectedDate[0]);
		const endDate = dayjs(contextValue.selectedDate[1]);
		return endDate.diff(startDate, 'day', true) >= 7;
	};

	useEffect(() => {
		if (contextValue.activatedInflowMode) {
			const isValid = validateInflowMode();
			if (!isValid) {
				setShowInflowWarningModal(true);
				setContextValue((prevContextValue) => ({
					...prevContextValue,
					activatedInflowMode: false,
				}));
			}
		}
	}, [contextValue.selectedDate, contextValue.selectedLocation, contextValue.activatedInflowMode]);

	useEffect(() => {
		if (selectedMode === 'dma' && !validateDmaDateRange()) {
			setShowDmaWarningModal(true);
			publishDashboardMapEvent({ type: 'resetDma' });
			publishDashboardMapEvent({ type: 'closePopup' });
		}
	}, [selectedMode, contextValue.selectedDate, setShowDmaWarningModal]);

	return { validateInflowMode, validateDmaDateRange, isWaterNetwork, isDrainageNetwork, analysisModeLabel, selectedModeLabel };
}
